import React, { useRef } from 'react'
import { connect } from 'react-redux'
import { setBlockProperties } from '../../../store/actions/blocks'
import { setTextEditMode } from '../../../store/actions/boards'
import ContentEditable from 'react-contenteditable'
import { findDOMNode } from 'react-dom'
import { withTranslation } from 'react-i18next'
import { colWidthSelector } from '../../../store/selectors/boardSelectors'
import styled from 'react-emotion'
import { compose, withHandlers, withState, lifecycle } from 'recompose'
import { editModeSelector } from '../../../store/selectors/selectors'
import sanitizeHtml from 'sanitize-html'
import useClickOutside from '../../../hooks/useClickOutside'
import linkify from 'linkifyjs/html'

const BlockText = styled(
  ({
    block,
    editing,
    editMode,
    edit,
    setText,
    exitEditMode,
    storeRef,
    className,
    t
  }) => {
    const ref = useRef(null)
    // exitEditMode on click outside
    useClickOutside(ref, editing && exitEditMode)

    const html = safeText(block.text.text, editMode)

    return (
      <ContentEditable
        className={className}
        onDoubleClick={edit}
        html={html}
        placeholder={t('block.enterText')}
        disabled={!editing}
        onChange={e => setText(e.target.value)}
        ref={storeRef}
        innerRef={ref}
        onClick={e => {
          if (editMode && e.target.tagName === 'A') {
            e.preventDefault()
          }
        }}
      />
    )
  }
)(({ block, editMode, editing, colWidth }) => ({
  Label: 'BlockText',
  lineHeight: block.text.size > 0.5 ? 1.2 : 1.45,
  textAlign: block.text.align || 'left',
  color: block.text.color || '',
  fontSize: block.text.size * colWidth,
  cursor: editMode && (editing ? 'text' : 'pointer'),
  wordBreak: 'break-word',

  // no column means placing
  border: !block.column && '1px solid #999',

  a: { textDecoration: editing ? 'none' : 'underline' },

  '&[contenteditable]:empty': {
    '&:before': {
      content: 'attr(placeholder)',
      display: 'inline' // For Firefox
    },
    '&:focus:before': {
      display: 'none'
    }
  }
}))

function safeText(text) {
  const sanitized = sanitizeHtml(text, {
    allowedTags: ['p', 'b', 'i', 'em', 'strong', 'a', 'br', 'div'],
    allowedAttributes: {
      a: ['href', 'target', 'rel']
    },
    transformTags: {
      a: sanitizeHtml.simpleTransform('a', {
        target: '_blank',
        rel: 'noopener noreferrer'
      })
    }
  })

  // sanitizeHtml may change nothing except the format of tags
  // eg <br> becomes <br />
  // passing sanitized through a div ensures that it's in the format the browser passed to us
  // therefore no change is registered and the text field is not re-rendered with the carret at start
  const div = document.createElement('div')
  div.innerHTML = sanitized

  return div.innerHTML
}

const BlockTextContainer = compose(
  connect(
    (state, { block }) => {
      const editing =
        editModeSelector(state) && state.board.textEditMode === block.id
      return {
        colWidth: colWidthSelector(state),
        editMode: editModeSelector(state),
        editing
      }
    },
    (dispatch, { block }) => ({
      setText: text =>
        dispatch(setBlockProperties(block, { 'text/text': safeText(text) })),
      setHeight: height =>
        dispatch(setBlockProperties(block, { height }, { noHistory: true })),
      edit: id => dispatch(setTextEditMode(id)),
      exitEditMode: () => dispatch(setTextEditMode(null))
    })
  ),
  withState('element', 'storeElement'),
  withHandlers({
    storeRef: props => ref => {
      const element = findDOMNode(ref)
      if (element && props.editing) element.focus()
      props.storeElement(element)
    },
    edit: ({ editMode, edit, block }) => () => editMode && edit(block.id)
  }),
  withTranslation(),
  lifecycle({
    componentDidUpdate({ block: old, editing: oldEditing }) {
      const {
        block,
        colWidth,
        editMode,
        setHeight,
        editing,
        element,
        setText
      } = this.props

      // focus on edit
      if (editing && !oldEditing) element.focus()

      // create links on done edit
      if (!editing && oldEditing) {
        setText(linkify(block.text.text, { className: '' }))
      }

      // Update height

      // only in edit mode
      if (!editMode) return
      // only for valid block
      if (!block.id || !block.column) return // (block probably in the queue)
      // only if there is no height set OR something that affects the text size has changed
      if (
        !block.height ||
        old.text.size !== block.text.size ||
        old.text.text !== block.text.text ||
        old.width !== block.width
      ) {
        const height = element.offsetHeight / colWidth
        // if different enough
        if (Math.round(height * 10) !== Math.round(block.height * 10))
          setHeight(height)
      }
    }
  })
)

export default BlockTextContainer(BlockText)
